import React, { InputHTMLAttributes, useState } from 'react'
import { Button } from '@/components/ui/button/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command/command'
import { Input, Label } from '@/components/ui/input/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover/popover'
import { cn } from '@/lib/utils'
import { AsYouType } from 'libphonenumber-js'
import { Check, ChevronDown } from 'lucide-react'
import { countries } from './countries'

export type Country = (typeof countries)[number]

interface PhoneInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'onChange'
> {
  value?: string
  defaultCountry?: string // Changed to string type to store ISO2 code
  onChange?: (fullPhoneNumber: string) => void // Function to handle full phone number change
  ValidityState?: (isValid: boolean) => boolean // Function to check validity state
  setErrorMessage: (message: string) => void
  label?: string
  isCountryBtnDisable?: boolean
  shouldAutoFocus?: boolean
  isOptional?: boolean
  dropdownDesabled?: boolean
  autoComplete?: string
}

export function PhoneInput({
  value: valueProp,
  defaultCountry = 'IN',
  onChange,
  ValidityState,
  setErrorMessage,
  label,
  isCountryBtnDisable,
  shouldAutoFocus,
  isOptional,
  dropdownDesabled = false,
  autoComplete = 'on',
  ...rest
}: Readonly<PhoneInputProps>) {
  const asYouType = new AsYouType()

  const [value, setValue] = useState(valueProp ?? '')
  const [openCommand, setOpenCommand] = useState(false)
  const [countryCode, setCountryCode] = useState(defaultCountry)
  const [error, setError] = useState(false) // State to manage error condition
  const [isInputFocused, setIsInputFocused] = useState(shouldAutoFocus ?? true)

  const selectedCountry = countries.find(
    (country) => country.iso2 === countryCode
  )

  const handleOnInput = (event: React.InputEvent<HTMLInputElement>) => {
    const input = event.currentTarget.value.trim()
    const formattedValue = asYouType.input(input)
    setValue(formattedValue)
    if (onChange && selectedCountry) {
      console.log('selected country', selectedCountry.phone_code)
      const fullPhoneNumber = `${selectedCountry.phone_code} ${formattedValue}`
      onChange(fullPhoneNumber)
      // Notify parent component of the full phone number change
    }
  }

  const handleOnBlur = () => {
    setIsInputFocused(false)
    let isValid = true
    if (isOptional) {
      setError(!isValid)
      return error
    }

    if (countryCode === 'IN') {
      if (value.length < 10) {
        isValid = false
        setErrorMessage('Mobile number must be at least 10 digits')
      } else if (value.length > 10) {
        isValid = false
        setErrorMessage("Mobile number can't exceed 10 digits")
      }
    }

    setError(!isValid)

    if (ValidityState) {
      ValidityState(isValid)
    }

    return error
  }

  const handleCountrySelect = (country: Country) => {
    setIsInputFocused(true)
    setCountryCode(country.iso2)
    setOpenCommand(false)
    const formattedValue = asYouType.input(value.trim())
    setValue('')
    setErrorMessage('')
    if (onChange) {
      const fullPhoneNumber = `${country.phone_code} ${formattedValue}`
      onChange(fullPhoneNumber)
    }
    ValidityState?.(false)
  }

  return (
    <div
      className={`relative flex items-center rounded-lg border border-gray-300 ${
        isInputFocused
          ? 'border-0 border-none shadow-fieldshadow outline-none ring-2 ring-ring'
          : ''
      }`}
    >
      <Label
        className={cn(
          'absolute -top-3 left-2 text-gray-500 text-primary',
          isInputFocused ? 'text-primary' : 'text-sm text-gray-500'
        )}
      >
        {label ?? 'Your Registered Mobile Number'}
      </Label>
      <Popover open={openCommand} onOpenChange={setOpenCommand} modal={true}>
        <PopoverTrigger asChild>
          <Button
            disabled={dropdownDesabled || isCountryBtnDisable}
            variant="ghost"
            onClick={() => setIsInputFocused(true)}
            aria-expanded={openCommand}
            className="w-max items-center justify-between whitespace-nowrap pl-4 pr-0"
            transactionName="forms.phone-input.country-select"
          >
            {selectedCountry?.name ? (
              <span className="relative top-2 font-semibold">
                {`${selectedCountry.emoji}`} +{selectedCountry.phone_code}
              </span>
            ) : (
              'Select country'
            )}
            <ChevronDown className="m-2 mb-1 mt-5 size-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-max p-0" align="start">
          <Command>
            <CommandInput placeholder="Search country..." />
            <CommandList>
              <CommandEmpty>No country found.</CommandEmpty>
              <CommandGroup>
                {countries.map((country) => (
                  <CommandItem
                    key={country.iso3}
                    value={`${country.name} (+${country.phone_code})`}
                    onSelect={() => handleCountrySelect(country)}
                    className="items-stretch"
                  >
                    <Check
                      className={cn(
                        'mr-2 size-4',
                        countryCode === country.iso2
                          ? 'opacity-100'
                          : 'opacity-0'
                      )}
                    />
                    <span className="relative top-0.5 mr-2 h-3 w-4 object-cover">
                      {country.emoji}
                    </span>
                    {country.name}{' '}
                    <span className="text-gray-11 ml-1">
                      (+{country.phone_code})
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <span className="text-custompipe">|</span>
      <div //NOSONAR
        className="grow"
        onBlur={handleOnBlur}
      >
        <Input
          autoComplete={autoComplete}
          disabled={isCountryBtnDisable}
          type="text"
          value={value}
          pattern="^[0-9]{1,}$"
          name="phone"
          autoFocus={shouldAutoFocus ?? true}
          className="!border-none pl-4 font-semibold leading-6 tracking-10 !shadow-none !outline-none !ring-transparent md:w-[14.4485rem]"
          onInput={handleOnInput}
          onClick={() => setIsInputFocused(true)}
          style={{ border: 'none' }}
          required={!isOptional}
          transactionName="login.phone-input"
          {...rest}
        />
      </div>
    </div>
  )
}
